import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Clock } from "lucide-react";
import { formatDateDE } from "@/lib/utils";
import { MWST_RATE } from "@/lib/vatConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StundenBestaetigen() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/inquiries/by-time-token/${token}`);
        if (!mounted) return;
        setData(res.data);
        if (res.data?.times_confirmed_at) setConfirmed(true);
      } catch (err) {
        if (!mounted) return;
        setError("Dieser Bestätigungslink ist ungültig oder abgelaufen.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/inquiries/by-time-token/${token}/confirm`, {});
      setConfirmed(true);
      toast.success("Vielen Dank — Stunden wurden bestätigt.");
    } catch (err) {
      toast.error("Bestätigung fehlgeschlagen. Bitte versuchen Sie es erneut.");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-red-700">Fehler</p>
          <h1 className="font-serif text-3xl mt-4">{error}</h1>
          <p className="text-sm text-[#757067] mt-4 font-light">
            Falls Sie eine aktuelle Stundenabrechnung erwarten, kontaktieren Sie uns bitte direkt.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-stunden-bestaetigen" className="min-h-screen bg-[#FAFAF7] py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Lukas Stutz — Mietkoch</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-4">Stundenabrechnung bestätigen</h1>
        {data.confirmation_number && (
          <p className="text-sm text-[#BA6D52] mt-3 tracking-wider">Auftragsnummer: <b>{data.confirmation_number}</b></p>
        )}

        <div className="mt-10 bg-white border border-[#E3DECE] p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Einsatz</p>
          <p className="font-serif text-2xl mt-2">{data.betrieb}</p>
          <p className="text-sm text-[#757067] mt-1">{data.ort} · ab {formatDateDE(data.einsatzdatum)}</p>

          <div className="mt-8">
            <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Erfasste Arbeitsstunden</p>
            {data.time_entries && data.time_entries.length > 0 ? (
              <div className="mt-4 border border-[#E3DECE]">
                <table className="w-full text-sm">
                  <thead className="bg-[#F2EFE9] text-xs uppercase tracking-wider text-[#BA6D52]">
                    <tr>
                      <th className="text-left p-3">Datum</th>
                      <th className="text-left p-3">Zeit</th>
                      <th className="text-right p-3">Stunden</th>
                      <th className="text-left p-3 hidden sm:table-cell">Vermerk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.time_entries.map((e) => (
                      <tr key={e.id} className="border-t border-[#E3DECE]">
                        <td className="p-3">{formatDateDE(e.date)}</td>
                        <td className="p-3 text-[#757067]">{e.von} – {e.bis}</td>
                        <td className="p-3 text-right font-medium">{Number(e.hours).toFixed(2).replace(".", ",")} h</td>
                        <td className="p-3 text-[#757067] text-xs italic hidden sm:table-cell">{e.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#F2EFE9]">
                    <tr>
                      <td colSpan={2} className="p-3 font-bold text-sm">Gesamt</td>
                      <td className="p-3 text-right font-serif text-xl text-[#BA6D52]">
                        {Number(data.total_hours).toFixed(2).replace(".", ",")} h
                      </td>
                      <td className="hidden sm:table-cell"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-sm text-[#757067] mt-3 italic">Keine Stunden erfasst.</p>
            )}
          </div>

          <div className="mt-6 bg-[#F2EFE9] border border-[#E3DECE] p-4 text-sm">
            {data.weekend_hours > 0 ? (
              <>
                <div className="flex justify-between">
                  <span className="text-[#757067]">Werktage ({Number(data.weekday_hours).toFixed(2).replace(".", ",")} h × {Number(data.hourly_rate_eur).toFixed(2).replace(".", ",")} €)</span>
                  <span>{(Number(data.weekday_hours) * Number(data.hourly_rate_eur)).toFixed(2).replace(".", ",")} €</span>
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-[#757067]">Wochenende/Feiertag ({Number(data.weekend_hours).toFixed(2).replace(".", ",")} h × {Number(data.hourly_rate_weekend_eur).toFixed(2).replace(".", ",")} €)</span>
                  <span>{(Number(data.weekend_hours) * Number(data.hourly_rate_weekend_eur)).toFixed(2).replace(".", ",")} €</span>
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-[#757067]">Lohnsumme ({Number(data.total_hours).toFixed(2).replace(".", ",")} h)</span>
                  <span>{Number(data.estimated_labor_total).toFixed(2).replace(".", ",")} €</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between">
                  <span className="text-[#757067]">Stundensatz</span>
                  <span>{Number(data.hourly_rate_eur).toFixed(2).replace(".", ",")} € / h netto</span>
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-[#757067]">Lohnsumme ({Number(data.total_hours).toFixed(2).replace(".", ",")} h)</span>
                  <span>{Number(data.estimated_labor_total).toFixed(2).replace(".", ",")} €</span>
                </div>
              </>
            )}
            {data.travel_cost_eur > 0 && (
              <div className="flex justify-between mt-2">
                <span className="text-[#757067]">Anfahrt</span>
                <span>{Number(data.travel_cost_eur).toFixed(2).replace(".", ",")} €</span>
              </div>
            )}
            <div className="flex justify-between mt-2 pt-2 border-t border-[#E3DECE]">
              <span className="text-[#757067]">Zwischensumme netto</span>
              <span>{Number(data.estimated_net_total || data.estimated_labor_total).toFixed(2).replace(".", ",")} €</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[#757067]">zzgl. {Math.round((data.mwst_rate || MWST_RATE) * 100)} % MwSt.</span>
              <span className="text-[#757067]">{Number(data.estimated_mwst || 0).toFixed(2).replace(".", ",")} €</span>
            </div>
            <div className="flex justify-between mt-2 pt-2 border-t-2 border-[#BA6D52]">
              <span className="font-medium">Endsumme brutto</span>
              <span className="font-serif text-xl text-[#BA6D52]">
                {Number(data.estimated_gross_total || data.estimated_labor_total).toFixed(2).replace(".", ",")} €
              </span>
            </div>
            <p className="text-xs text-[#757067] italic mt-2">Inkl. ges. MwSt. — Zahlungsfrist 7 Tage netto.</p>
          </div>
        </div>

        {/* Action */}
        {confirmed ? (
          <div data-testid="stunden-confirmed" className="mt-8 bg-emerald-50 border border-emerald-200 p-6 md:p-8 text-center">
            <CheckCircle2 size={48} className="mx-auto text-emerald-600" />
            <h2 className="font-serif text-2xl mt-4 text-emerald-900">Stunden erfolgreich bestätigt</h2>
            <p className="text-sm text-emerald-800 mt-2">
              Vielen Dank! Die Bestätigung wurde dokumentiert. Ihre Rechnung wird automatisch erstellt
              und Ihnen in wenigen Minuten per E-Mail zugesendet.
            </p>
          </div>
        ) : (
          <div className="mt-8 bg-[#FAF5F1] border border-[#BA6D52] p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Verbindliche Bestätigung</p>
            <p className="text-sm text-[#2C2A26] mt-3 font-light leading-relaxed">
              Mit einem Klick auf den Button bestätigen Sie verbindlich, dass die oben aufgeführten
              Arbeitsstunden korrekt erfasst wurden und durch Lukas Stutz tatsächlich erbracht worden sind.
              Die Bestätigung wird mit Zeitstempel dokumentiert.
            </p>
            <button
              onClick={handleConfirm}
              disabled={confirming}
              data-testid="stunden-confirm-btn"
              className="mt-6 w-full sm:w-auto bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-8 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-3"
            >
              {confirming ? (
                <><Loader2 size={16} className="animate-spin" /> Bestätigung wird gespeichert …</>
              ) : (
                <><Clock size={16} /> Stunden verbindlich bestätigen</>
              )}
            </button>
            <p className="text-xs text-[#757067] italic mt-4">
              Sollten Korrekturen erforderlich sein, antworten Sie bitte direkt auf die E-Mail.
              Wird nicht innerhalb von 7 Tagen widersprochen, gelten die Stunden als anerkannt.
            </p>
          </div>
        )}

        <p className="text-xs text-[#757067] text-center mt-12">
          Lukas Stutz — Mietkoch für Gastronomie &amp; Events
        </p>
      </div>
    </div>
  );
}
